import { type FC } from "react";
import { useTranslation } from "../../../contexts/useTranslation";
import { type User } from "../../../core/types";
import { getActiveSupportAgents } from "../../../utils/userUtil";
import { useWidget } from "../../../contexts/useWidget";
import { useSupportAgents } from "../../../contexts/useSupportAgents";
import Avatar from "../../../components/Avatar";
import { Slot, Templated } from "../../../templating";
import type { TranslateFn } from "../../../contexts/TranslationContext";
import {
  getResponseTimeTranslation,
  getSupportAgentCanJoinInTranslation,
  getSupportAgentNameTranslation
} from "../../../utils/translationUtil";

interface SupportAgentStatusProps {
  invitedSupportAgent: User | null;
}

const getSupportAgentStatusText = (translate: TranslateFn, supportAgent: User | null, invited: boolean, averageResponseTime: number): string => {
  if (supportAgent && invited) {
    return translate("support_agent_will_join_you_in_a_moment", { support_agent: getSupportAgentNameTranslation(translate, supportAgent) });
  } else if (supportAgent && !invited) {
    return getSupportAgentCanJoinInTranslation(translate, supportAgent, averageResponseTime);
  } else {
    return getResponseTimeTranslation(translate, averageResponseTime);
  }
};

const SupportAgentStatus: FC<SupportAgentStatusProps> = ({ invitedSupportAgent }) => {
  const { translate } = useTranslation();
  const { averageResponseTime } = useWidget();
  const { supportAgents } = useSupportAgents();
  const targetAgent = invitedSupportAgent ? invitedSupportAgent : getActiveSupportAgents(supportAgents)[0];

  return (
    <Templated id="lastbot-support-agent-status">
      <div className="flex items-center justify-center gap-x-2 h-[26px] mt-3">
        {targetAgent && (
          <div className="flex">
            <Slot name="avatar">
              <Avatar
                url={targetAgent.avatar_url}
                name={getSupportAgentNameTranslation(translate, targetAgent)}
              />
            </Slot>
          </div>
        )}
        <div className="text-xs text-lastbot-icon-muted">
          <Slot name="name">{targetAgent ? getSupportAgentNameTranslation(translate, targetAgent) : ""}</Slot>
          <Slot name="status-text">
            {getSupportAgentStatusText(translate, targetAgent, invitedSupportAgent !== null, averageResponseTime)}
          </Slot>
        </div>
      </div>
    </Templated>
  );
};

export default SupportAgentStatus;
